/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import { select } from 'redux-saga/effects';
import { SagaIterator } from 'redux-saga';
import { Asset, AssetBookmark, BookmarkUtils, PlayableAsset, WatchState, Ducks } from '@wit/vtv-x-foundation-lib';
import { SeasonsData } from '../seasons';

function getAssetBookmark(bookmarksCache: Ducks.BookmarksCache.ReducerState, asset: Asset): AssetBookmark | undefined {
  const id = BookmarkUtils.getBookmarkAssetId(asset);
  return bookmarksCache[id]?.data;
}

function getCloneWithLongestPosition(
  clones: Asset[],
  bookmarksCache: Ducks.BookmarksCache.ReducerState
): [Asset, number] {
  let maxEpisode: Asset = clones[0];
  let maxPosition = 0;
  clones.forEach((episodeClone) => {
    const bookmark: AssetBookmark | undefined = getAssetBookmark(bookmarksCache, episodeClone);
    if (bookmark && bookmark.position > maxPosition) {
      maxPosition = bookmark.position;
      maxEpisode = episodeClone;
    }
  });

  return [maxEpisode, maxPosition] as [Asset, number];
}

function* getBestEpisode(seasonsData: SeasonsData): SagaIterator<Asset | undefined> {
  const bookmarksCache: Ducks.BookmarksCache.ReducerState = yield select(Ducks.BookmarksCache.Selectors.getBookmarks);

  let clones: Asset[];
  let partialWatched: Asset | undefined; // latest partial watched
  let watched: Asset | undefined;
  let unwatchedAfterWatched: Asset | undefined; // first unwatched episode after a previous completed watched
  let firstUnwatched: Asset | undefined; // first unwatched episode from all seasons

  Object.entries(seasonsData.episodesBySeason).forEach(([, episodes]) => {
    episodes.forEach((episode) => {
      // calculate the watch state for each clones set
      // clones are sorted using the following order: 1-vods, 2-epg and 3-recoprdings
      clones = seasonsData.episodesClones[(episode as PlayableAsset).internalId];
      const [maxEpisode, maxPosition]: [Asset, number] = getCloneWithLongestPosition(clones, bookmarksCache);
      const watchState = BookmarkUtils.getWatchState(maxEpisode as PlayableAsset, maxPosition);

      switch (watchState) {
        case WatchState.WATCHING:
          partialWatched = maxEpisode;
          break;
        case WatchState.WATCHED:
          watched = maxEpisode;
          break;
        default:
          if (watched) {
            // if there is a previous watched then updates the unwatchedAfterWatched
            unwatchedAfterWatched = maxEpisode;
            watched = undefined;
          } else if (!firstUnwatched) {
            firstUnwatched = maxEpisode; // keep the first unwatched
          }
          break;
      }
    });
  });

  const unwatched = unwatchedAfterWatched || firstUnwatched;

  return partialWatched || unwatched; // can return undefined if all the epsides are watched
}

export default {
  getBestEpisode,
};
